import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, QueryChips } from '@teals/ui-web';
import { useSendMessageMutation } from '../../store/api';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import type { ExchangeEvent } from '../../hooks/useWebSocket';
import {
  configsDiffer,
  parseBuilderResponse,
  toProposed,
  type AgentConfigSnapshot,
  type ChatMessage,
  type ProposedConfig,
} from './builderHelpers';

const BUILDER_AGENT = 'agent-builder';

const ChatShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 28rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  overflow: hidden;
`;

const ChatHeader = styled.div`
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid var(--border);
`;

const ChatTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
`;

const ChatSubtitle = styled.div`
  margin-top: 0.25rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 16rem;
  max-height: 28rem;
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  padding: 0.65rem 0.85rem;
  border-radius: 12px;
  white-space: pre-wrap;
  font-size: 0.875rem;
  line-height: 1.45;
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent-muted, #e0f2fe)' : 'var(--surface-2, #f8fafc)'};
  color: var(--text);
`;

const PendingBubble = styled.div`
  align-self: flex-start;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const Dropzone = styled.div<{ $over?: boolean }>`
  margin: 0 1rem 1rem;
  padding: 0.75rem;
  border: 1px dashed
    ${({ $over }) => ($over ? 'var(--accent)' : 'var(--border)')};
  border-radius: 10px;
  background: ${({ $over }) =>
    $over ? 'var(--accent-muted, #ecfeff)' : 'transparent'};
`;

const DropLabel = styled.div<{ $over?: boolean }>`
  font-size: 0.75rem;
  color: ${({ $over }) => ($over ? 'var(--accent)' : 'var(--text-muted)')};
  margin-bottom: 0.5rem;
`;

const AttachedRefs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
`;

const AttachedRef = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: var(--surface-2, #f1f5f9);
`;

const ChipRemove = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 0.1rem;
  color: var(--text-muted);
`;

const ChatInputRow = styled.form`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const ChatInput = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 2.5rem;
  max-height: 8rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  font: inherit;
  background: var(--surface);
  color: var(--text);
`;

const ChipWrap = styled.div`
  margin-top: 0.75rem;
`;

const DiffCard = styled.div`
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  font-size: 0.875rem;
`;

const DiffActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const ErrorBanner = styled.div`
  margin-top: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  background: #fef2f2;
  color: #991b1b;
  font-size: 0.8125rem;
`;

export interface BuilderAcceptArgs {
  proposed: ProposedConfig;
  agentConfig: Record<string, unknown>;
  conversationId?: string;
}

export interface BuilderChatProps {
  mode: 'create' | 'configure';
  greeting: string;
  subtitle: string;
  placeholder: string;
  seedChips: string[];
  promptContext: Record<string, unknown>;
  metadataSource: 'create' | 'configure';
  editsEnabled: boolean;
  inputDisabled?: boolean;
  currentConfig: AgentConfigSnapshot;
  fleetNames: string[];
  dataSourceNames: string[];
  appliedMessage?: string;
  onAccept: (args: BuilderAcceptArgs) => Promise<void>;
  onChipClick?: (chip: string) => boolean | void;
  onPendingChange?: (pending: ProposedConfig | null) => void;
  onContentChange?: (hasUserContent: boolean) => void;
}

const BuilderChat: React.FC<BuilderChatProps> = ({
  mode,
  greeting,
  subtitle,
  placeholder,
  seedChips,
  promptContext,
  metadataSource,
  editsEnabled,
  inputDisabled = false,
  currentConfig,
  fleetNames,
  dataSourceNames,
  appliedMessage,
  onAccept,
  onChipClick,
  onPendingChange,
  onContentChange,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    { id: 'greeting', role: 'assistant', content: greeting },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [attachedRefs, setAttachedRefs] = useState<string[]>([]);
  const [dropOver, setDropOver] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [pending, setPending] = useState<ProposedConfig | null>(null);
  const [pendingRaw, setPendingRaw] = useState<Record<string, unknown> | null>(
    null,
  );
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const greetedRef = useRef(false);

  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const { subscribe, unsubscribe, addListener, removeListener } =
    useWebSocketContext();

  const showPending = isSending || awaitingReply;
  const locked = inputDisabled || showPending;

  const configRef = useRef(currentConfig);
  const fleetRef = useRef(fleetNames);
  const dsNamesRef = useRef(dataSourceNames);
  configRef.current = currentConfig;
  fleetRef.current = fleetNames;
  dsNamesRef.current = dataSourceNames;

  useEffect(() => {
    if (greetedRef.current) return;
    greetedRef.current = true;
    setMessages([{ id: 'greeting', role: 'assistant', content: greeting }]);
  }, [greeting]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, awaitingReply]);

  useEffect(() => {
    if (!sessionId) return;
    subscribe(sessionId, BUILDER_AGENT);
    return () => unsubscribe();
  }, [sessionId, subscribe, unsubscribe]);

  useEffect(() => {
    onPendingChange?.(pending);
  }, [pending, onPendingChange]);

  useEffect(() => {
    const hasUser = messages.some((m) => m.role === 'user');
    onContentChange?.(hasUser);
  }, [messages, onContentChange]);

  const applyAssistantText = useCallback(
    (content: string) => {
      const parsed = parseBuilderResponse(content);
      const message =
        parsed.message ||
        (parsed.agentConfig
          ? 'Proposed configuration — review and Accept or Discard.'
          : content);
      setMessages((prev) => {
        if (prev.some((m) => m.content === message && m.role === 'assistant')) {
          return prev;
        }
        return [
          ...prev,
          { id: `a-${Date.now()}`, role: 'assistant', content: message },
        ];
      });
      if (parsed.suggestions?.length) {
        setSuggestions(parsed.suggestions);
      }
      if (parsed.agentConfig && editsEnabled) {
        const proposed = toProposed(parsed.agentConfig);
        const differs =
          mode === 'create'
            ? Boolean(
                proposed.agentName ||
                  proposed.systemPrompt ||
                  proposed.model,
              )
            : configsDiffer(
                configRef.current,
                fleetRef.current,
                dsNamesRef.current,
                proposed,
              );
        if (differs) {
          setPending(proposed);
          setPendingRaw(parsed.agentConfig);
        }
      }
    },
    [editsEnabled, mode],
  );

  useEffect(() => {
    const listener = (event: ExchangeEvent) => {
      if (
        (event.type === 'exchange.response.completed' ||
          event.type === 'conversation.result' ||
          event.type === 'exchange.completed') &&
        event.data?.response
      ) {
        const content = String(event.data.response);
        setAwaitingReply(false);
        applyAssistantText(content);
      }
      if (
        event.type === 'conversation.result' &&
        (event.data?.status === 'failed' || event.data?.error)
      ) {
        setAwaitingReply(false);
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: 'assistant',
            content: String(
              event.data?.error ||
                event.data?.message ||
                'Configuration assistant failed.',
            ),
          },
        ]);
      }
    };
    addListener(listener);
    return () => removeListener(listener);
  }, [addListener, removeListener, applyAssistantText]);

  const displayChips = suggestions.length ? suggestions : seedChips;

  const sendQuery = async (query: string, refs?: string[]) => {
    const attached = refs ?? attachedRefs;
    const text = query.trim();
    if ((!text && !attached.length) || locked) return;

    const refPrefix =
      attached.length > 0
        ? attached.map((n) => `$DataSource:${n}`).join(' ') + '\n'
        : '';
    const fullQuery = `${refPrefix}${text}`.trim();

    setChatInput('');
    setAttachedRefs([]);
    setActionError(null);
    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', content: fullQuery },
    ]);
    setAwaitingReply(true);
    try {
      const result = await sendMessage({
        query: fullQuery,
        agentName: BUILDER_AGENT,
        sessionId,
        metadata: {
          source: metadataSource,
          prompt_context: promptContext,
          attached_data_sources: attached,
        },
      }).unwrap();
      if (result.sessionId) {
        setSessionId(result.sessionId);
        subscribe(result.sessionId, BUILDER_AGENT);
      }
      if (result.conversationId) {
        setConversationId(result.conversationId);
      }
      const replyText = (result.response || '').trim();
      const pendingAsync =
        result.isAsync === true ||
        result.status === 'processing' ||
        (!replyText && Boolean(result.conversationId));
      if (pendingAsync) return;
      setAwaitingReply(false);
      if (replyText) applyAssistantText(replyText);
    } catch (err: unknown) {
      setAwaitingReply(false);
      const status =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status?: number }).status)
          : undefined;
      const message =
        status === 404
          ? 'Agent Builder is not available in this environment.'
          : 'Unable to reach the configuration assistant.';
      setActionError(message);
      setMessages((prev) => [
        ...prev,
        { id: `e-${Date.now()}`, role: 'assistant', content: message },
      ]);
    }
  };

  const handleChipSelect = (chip: string) => {
    if (locked) return;
    if (onChipClick?.(chip)) return;
    void sendQuery(chip);
  };

  const onDropDs = (name: string) => {
    setAttachedRefs((prev) => (prev.includes(name) ? prev : [...prev, name]));
    if (!chatInput.trim()) {
      setChatInput(`Associate and use ${name} in the system prompt.`);
    }
  };

  const handleAccept = async () => {
    if (!pending || !pendingRaw || !editsEnabled) return;
    setSaving(true);
    setActionError(null);
    try {
      await onAccept({
        proposed: pending,
        agentConfig: pendingRaw,
        conversationId,
      });
      setPending(null);
      setPendingRaw(null);
      if (appliedMessage) {
        setMessages((prev) => [
          ...prev,
          {
            id: `ok-${Date.now()}`,
            role: 'assistant',
            content: appliedMessage,
          },
        ]);
      }
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to apply proposal',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPending(null);
    setPendingRaw(null);
  };

  const fleetDiffLines = useMemo(() => {
    if (!pending?.fleet) return [];
    const current = new Set(fleetNames);
    const next = new Set(pending.fleet);
    const removed = fleetNames.filter((n) => !next.has(n));
    const added = pending.fleet.filter((n) => !current.has(n));
    return [...removed.map((n) => `− ${n}`), ...added.map((n) => `+ ${n}`)];
  }, [pending, fleetNames]);

  return (
    <div>
      <ChatShell>
        <ChatHeader>
          <ChatTitle>Agent Builder</ChatTitle>
          <ChatSubtitle>{subtitle}</ChatSubtitle>
        </ChatHeader>
        <MessageList>
          {messages.map((m) => (
            <Bubble key={m.id} $role={m.role}>
              {m.content}
            </Bubble>
          ))}
          {showPending && (
            <PendingBubble role="status">Thinking…</PendingBubble>
          )}
          <div ref={endRef} />
        </MessageList>
        <Dropzone
          $over={dropOver}
          data-testid="builder-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDropOver(true);
          }}
          onDragLeave={() => setDropOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDropOver(false);
            const name = e.dataTransfer.getData('text/plain');
            if (name) onDropDs(name);
          }}
        >
          <DropLabel $over={dropOver}>
            {dropOver ? 'Drop data source here' : 'Agent Builder dropzone'}
          </DropLabel>
          {attachedRefs.length > 0 && (
            <AttachedRefs>
              {attachedRefs.map((name) => (
                <AttachedRef key={name}>
                  $DataSource:{name}
                  <ChipRemove
                    type="button"
                    aria-label={`Remove ${name}`}
                    onClick={() =>
                      setAttachedRefs((prev) =>
                        prev.filter((n) => n !== name),
                      )
                    }
                  >
                    ×
                  </ChipRemove>
                </AttachedRef>
              ))}
            </AttachedRefs>
          )}
          <ChatInputRow
            onSubmit={(e) => {
              e.preventDefault();
              void sendQuery(chatInput);
            }}
          >
            <ChatInput
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={placeholder}
              disabled={locked}
              rows={1}
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={
                (!chatInput.trim() && !attachedRefs.length) || locked
              }
            >
              Send
            </Button>
          </ChatInputRow>
        </Dropzone>
      </ChatShell>

      {displayChips.length > 0 && (
        <ChipWrap>
          <QueryChips
            chips={displayChips.map((label) => ({ label }))}
            onSelect={handleChipSelect}
          />
        </ChipWrap>
      )}

      {pending && (
        <DiffCard>
          <strong>Proposed configuration change</strong>
          <div style={{ marginTop: '0.5rem' }}>
            {pending.agentName && <div>Name: {pending.agentName}</div>}
            {pending.agentType && <div>Type: {pending.agentType}</div>}
            {pending.model != null && pending.model !== currentConfig.model && (
              <div>
                Model: {currentConfig.model || '—'} → {pending.model}
              </div>
            )}
            {pending.temperature != null &&
              pending.temperature !== currentConfig.temperature && (
                <div>
                  Temperature: {currentConfig.temperature} →{' '}
                  {pending.temperature}
                </div>
              )}
            {pending.systemPrompt != null &&
              pending.systemPrompt !== currentConfig.systemPrompt && (
                <div>
                  System prompt updated
                  {pending.systemPrompt.includes('$DataSource:')
                    ? ' (includes $DataSource: directive)'
                    : ''}
                </div>
              )}
            {pending.recommendedModels && (
              <div>
                Recommended: {pending.recommendedModels.join(', ') || '—'}
              </div>
            )}
            {pending.dataSources && (
              <div>
                Data sources: {pending.dataSources.join(', ') || '—'}
              </div>
            )}
            {fleetDiffLines.length > 0 && (
              <div
                style={{
                  marginTop: '0.35rem',
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '0.75rem',
                }}
              >
                {fleetDiffLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
                <div>({pending.fleet?.length ?? 0} specialists)</div>
              </div>
            )}
          </div>
          <DiffActions>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleAccept()}
              disabled={saving || !editsEnabled}
            >
              Accept
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscard}
              disabled={saving}
            >
              Discard
            </Button>
          </DiffActions>
        </DiffCard>
      )}

      {actionError && <ErrorBanner>{actionError}</ErrorBanner>}
    </div>
  );
};

export default BuilderChat;
